import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Calendar,
  Plus,
  LayoutGrid,
  Table,
  Briefcase,
  Ticket,
} from "lucide-react";
import { toast } from "react-toastify";
import eventService from "../../../../api/EventService";
import Loader from "../../../../components/misc/Loader";
import EventStatsCard from "../events/components/EventStatsCard";
import ConferenceTradeMissionFilterSection from "./components/ConferenceTradeMissionFilterSection";
import ConferenceTradeMissionTable from "./components/ConferenceTradeMissionTable";

/**
 * Trade Missions Management Component
 */
const TradeMissions = () => {
  const navigate = useNavigate();

  // State management
  const [events, setEvents] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");
  const [sortBy, setSortBy] = useState("date");
  const [sortOrder, setSortOrder] = useState("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("card");

  // Fetch events
  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const response = await eventService.getEvents();
      if (response?.status && response?.data) {
        let eventsData = [];
        if (response.data.status_code === 200 && response.data.data) {
          eventsData = Array.isArray(response.data.data)
            ? response.data.data
            : [response.data.data];
        } else if (Array.isArray(response.data)) {
          eventsData = response.data;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          eventsData = response.data.data;
        }
        
        // Filter for trade missions only
        const filteredEvents = eventsData.filter((event) => {
          if (!event.event_type || !Array.isArray(event.event_type)) {
            return false;
          }
          return event.event_type.includes("trade_mission");
        });
        
        setEvents(filteredEvents);
      } else {
        toast.error(
          response?.message || "Failed to fetch trade missions"
        );
        setEvents([]);
      }
    } catch (error) {
      console.error("ERROR: ", error);
      toast.error(
        error.response?.data?.reason ||
          "An error occurred while fetching trade missions"
      );
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  // Calculate statistics
  const stats = useMemo(() => {
    const total = events.length;
    const upcoming = events.filter((event) => {
      if (!event.event_date) return false;
      const eventDate = new Date(event.event_date);
      return eventDate >= new Date();
    }).length;
    const closed = events.filter((event) => {
      const state = (event.event_state || event.status || "").toLowerCase();
      return state === "closed";
    }).length;

    return {
      total,
      upcoming,
      closed,
    };
  }, [events]);

  // Filter and search logic
  const filteredEvents = useMemo(() => {
    return events
      .filter((event) => {
        const matchesSearch =
          (event.event_title || event.venue_name || "")
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          (event.venue_name || "")
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          (event.city || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
          (event.country || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
          (event.about_event || "").toLowerCase().includes(searchTerm.toLowerCase());

        const matchesStatus =
          statusFilter === "all" ||
          (statusFilter === "upcoming" &&
            event.event_date &&
            new Date(event.event_date) >= new Date()) ||
          (statusFilter === "past" &&
            event.event_date &&
            new Date(event.event_date) < new Date()) ||
          (statusFilter === "closed" && (event.event_state || event.status || "").toLowerCase() === "closed") ||
          (statusFilter === "active" && (event.event_state || event.status || "").toLowerCase() !== "closed");

        const matchesType =
          typeFilter === "all" ||
          (Array.isArray(event.event_type)
            ? event.event_type.includes(typeFilter)
            : event.event_type === typeFilter);

        const matchesDate =
          dateFilter === "all" ||
          (dateFilter === "today" &&
            event.event_date &&
            new Date(event.event_date).toDateString() ===
              new Date().toDateString()) ||
          (dateFilter === "thisWeek" &&
            event.event_date &&
            (() => {
              const eventDate = new Date(event.event_date);
              const today = new Date();
              const weekFromNow = new Date(today);
              weekFromNow.setDate(today.getDate() + 7);
              return eventDate >= today && eventDate <= weekFromNow;
            })()) ||
          (dateFilter === "thisMonth" &&
            event.event_date &&
            (() => {
              const eventDate = new Date(event.event_date);
              const today = new Date();
              return (
                eventDate.getMonth() === today.getMonth() &&
                eventDate.getFullYear() === today.getFullYear()
              );
            })());

        return matchesSearch && matchesStatus && matchesType && matchesDate;
      })
      .sort((a, b) => {
        let aValue, bValue;

        switch (sortBy) {
          case "name":
            aValue = (a.event_title || a.venue_name || "").toLowerCase();
            bValue = (b.event_title || b.venue_name || "").toLowerCase();
            break;
          case "date":
            aValue = new Date(a.event_date || 0);
            bValue = new Date(b.event_date || 0);
            break;
          case "venue":
            aValue = (a.venue_name || "").toLowerCase();
            bValue = (b.venue_name || "").toLowerCase();
            break;
          default:
            aValue = a[sortBy] || "";
            bValue = b[sortBy] || "";
        }

        if (sortOrder === "asc") {
          return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
        } else {
          return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
        }
      });
  }, [events, searchTerm, statusFilter, typeFilter, dateFilter, sortBy, sortOrder]);

  // Pagination
  const paginatedEvents = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredEvents.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredEvents, currentPage, itemsPerPage]);

  // Handlers
  const handleViewDetails = (event) => {
    if (event.slug || event.event_slug) {
      const slug = event.slug || event.event_slug;
      navigate(`/admin/media/trade-mission/view/${slug}`, {
        state: { eventData: event }
      });
    }
  };

  const handleEdit = (event) => {
    if (event.slug || event.event_slug) {
      navigate(`/admin/media/conferences-trade-missions/edit/${event.slug || event.event_slug}`, {
        state: { eventData: event, from: "/admin/media/trade-missions", eventType: "trade_mission" }
      });
    }
  };

  const handleViewAnalytics = (event) => {
    if (event.slug || event.event_slug) {
      const slug = event.slug || event.event_slug;
      navigate(`/admin/media/conferences-trade-missions/view-trade-mission/${slug}/analytics`, {
        state: { eventData: event }
      });
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Loader />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col justify-between space-y-4 sm:flex-row sm:items-center sm:space-y-0">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">
            Trade Missions
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            Manage and monitor all trade missions
          </p>
        </div>
        <button
          onClick={() => navigate("/admin/media/conferences-trade-missions/create", {
            state: { eventType: "trade_mission", from: "/admin/media/trade-missions" }
          })}
          className="flex items-center justify-center px-4 py-2 text-sm font-semibold text-white bg-gradient-to-r from-orange-600 to-orange-700 rounded-lg shadow-sm transition-all hover:from-orange-700 hover:to-orange-800 hover:shadow-md"
        >
          <Plus className="mr-2 w-4 h-4" />
          Create New
        </button>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <EventStatsCard
          title="Total Trade Missions"
          value={stats.total}
          icon={Briefcase}
          color="orange"
        />
        <EventStatsCard
          title="Upcoming"
          value={stats.upcoming}
          icon={Calendar}
          color="green"
        />
        <EventStatsCard
          title="Closed"
          value={stats.closed}
          icon={Ticket}
          color="gray"
        />
      </div>

      {/* Filters and Search */}
      <ConferenceTradeMissionFilterSection
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        statusFilter={statusFilter}
        onStatusFilterChange={(value) => {
          setStatusFilter(value);
          setCurrentPage(1);
        }}
        typeFilter={typeFilter}
        onTypeFilterChange={(value) => {
          setTypeFilter(value);
          setCurrentPage(1);
        }}
        dateFilter={dateFilter}
        onDateFilterChange={(value) => {
          setDateFilter(value);
          setCurrentPage(1);
        }}
        sortBy={sortBy}
        onSortByChange={(value) => {
          setSortBy(value);
          setCurrentPage(1);
        }}
        sortOrder={sortOrder}
        onSortOrderChange={setSortOrder}
        onRefresh={fetchEvents}
        hideTypeFilter={true}
        eventType="trade_mission"
      />

      {/* View Toggle and Table Section */}
      <div className="space-y-4">
        {/* View Toggle */}
        <div className="flex justify-end items-center">
          <div className="inline-flex p-1 bg-white border border-gray-200 rounded-lg shadow-sm">
            <button
              onClick={() => setViewMode("table")}
              className={`flex items-center px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                viewMode === "table"
                  ? "text-white bg-orange-600 shadow-sm"
                  : "text-gray-700 hover:text-gray-900 hover:bg-gray-50"
              }`}
            >
              <Table className="mr-2 w-4 h-4" />
              Table
            </button>
            <button
              onClick={() => setViewMode("card")}
              className={`flex items-center px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                viewMode === "card"
                  ? "text-white bg-orange-600 shadow-sm"
                  : "text-gray-700 hover:text-gray-900 hover:bg-gray-50"
              }`}
            >
              <LayoutGrid className="mr-2 w-4 h-4" />
              Card
            </button>
          </div>
        </div>

        {/* Table/Card Section */}
        <ConferenceTradeMissionTable
          events={paginatedEvents}
          onView={handleViewDetails}
          onEdit={handleEdit}
          onViewAnalytics={handleViewAnalytics}
          currentPage={currentPage}
          itemsPerPage={itemsPerPage}
          totalCount={filteredEvents.length}
          onPageChange={setCurrentPage}
          onItemsPerPageChange={(value) => {
            setItemsPerPage(value);
            setCurrentPage(1);
          }}
          viewMode={viewMode}
          onRefresh={fetchEvents}
        />
      </div>
    </div>
  );
};

export default TradeMissions;

